/**
 * Хранилище личного пространства в IndexedDB.
 *
 * Пространство сериализуется целиком: оно небольшое, а частичные обновления
 * потребовали бы схемы миграций для каждого поля. Ключ — installationId, что
 * изолирует пространства разных установок в одном браузере (ТЗ FR-WS-04).
 */

import type { InstallationId } from '@type2x/shared-contracts';
import type {
  StoredWorkspace as PersistedWorkspace,
  Workspace,
  WorkspaceRepository,
} from '@type2x/feature-personal-workspace';
import { WORKSPACE_SCHEMA_VERSION, migrateWorkspace } from '@type2x/feature-personal-workspace';

import {
  WORKSPACE_STORE,
  deleteRecord,
  openDatabase,
  readRecord,
  writeRecord,
} from './indexeddb.js';

interface StoredRecord {
  readonly installationId: InstallationId;
  readonly schemaVersion: number;
  readonly workspace: PersistedWorkspace;
}

/** Ошибка несовместимой версии локальной схемы. */
export class SchemaVersionError extends Error {
  constructor(found: number) {
    super(`Несовместимая версия локального хранилища: ${found}`);
    this.name = 'SchemaVersionError';
  }
}

/** Репозиторий пространства поверх IndexedDB. */
export function createIndexedDbWorkspaceRepository(dbName?: string): WorkspaceRepository {
  const connection = openDatabase(dbName);

  return {
    async load(installationId) {
      const db = await connection;
      const stored = await readRecord<StoredRecord>(db, WORKSPACE_STORE, installationId);
      if (stored === null) return null;

      // Более новая схема из другой вкладки или версии приложения: молча
      // читать её нельзя — данные могут быть интерпретированы неверно.
      if (stored.schemaVersion > WORKSPACE_SCHEMA_VERSION) {
        throw new SchemaVersionError(stored.schemaVersion);
      }
      // Запись прежней версии дополняется значениями по умолчанию: новое поле
      // не повод потерять прогресс.
      return migrateWorkspace(stored.workspace, new Date().toISOString());
    },

    async save(workspace) {
      const db = await connection;
      await writeRecord(db, WORKSPACE_STORE, {
        installationId: workspace.owner.installationId,
        schemaVersion: workspace.schemaVersion,
        workspace,
      } satisfies StoredRecord);
    },

    async clear(installationId) {
      const db = await connection;
      await deleteRecord(db, WORKSPACE_STORE, installationId);
    },
  };
}

/**
 * Репозиторий в памяти.
 *
 * Используется в тестах и как запасной путь, когда IndexedDB недоступна
 * (приватный режим части браузеров): тренировка должна работать и без
 * сохранения, потеряв только историю.
 */
export function createInMemoryWorkspaceRepository(): WorkspaceRepository {
  const storage = new Map<string, Workspace>();

  return {
    load: (installationId) => Promise.resolve(storage.get(installationId) ?? null),
    save: (workspace) => {
      storage.set(workspace.owner.installationId, workspace);
      return Promise.resolve();
    },
    clear: (installationId) => {
      storage.delete(installationId);
      return Promise.resolve();
    },
  };
}
